// Auth-related navigation helpers

"use strict";

import { useRoute, useRouter, type RouteLocationRaw } from "vue-router";
import { AuthController } from "@/control/auth";
import { HOME_ROUTE, UNAUTHORIZED_ONLY_ROUTES } from "@/navigation-constants";
import { clone } from "@/utils/objects";

/**
 * Clears the session and redirects the user to the login page.
 * @param router Vue Router instance
 * @param route Current route, used to remember where to return after login
 * @param closeModal Callback to force-close any open modal
 * @param isReentrant Optional guard that returns true if a redirect is already in progress
 * @param setReentrant Optional setter for the re-entrancy guard
 */
export function requireLoginNavigation(
    router: ReturnType<typeof useRouter>,
    route: ReturnType<typeof useRoute> | undefined,
    closeModal: (forced?: boolean) => void,
    isReentrant?: () => boolean,
    setReentrant?: (value: boolean) => void,
): void {
    if (isReentrant?.()) {
        return;
    }

    setReentrant?.(true);
    AuthController.ClearSession();

    const goBackRoute: RouteLocationRaw = route
        ? {
              name: route.name,
              params: clone(route.params),
              query: clone(route.query),
          }
        : { name: HOME_ROUTE };

    if (typeof goBackRoute.name === "string" && !UNAUTHORIZED_ONLY_ROUTES.includes(goBackRoute.name)) {
        AuthController.PageToGo = {
            name: goBackRoute.name,
            params: "params" in goBackRoute ? goBackRoute.params : undefined,
            query: "query" in goBackRoute ? goBackRoute.query : undefined,
        };
    }

    closeModal(true);

    if (goBackRoute.name !== "login") {
        router.push({ name: "login" });
    }

    setReentrant?.(false);
}

/**
 * Navigates back to the route saved before login was required.
 * @param router Vue Router instance
 */
export function goBackFromLoginNavigation(router: ReturnType<typeof useRouter>): void {
    router.push({
        name: AuthController.PageToGo.name,
        params: AuthController.PageToGo.params,
        query: AuthController.PageToGo.query,
    });
}

/**
 * Composable for auth-related navigation from protected pages.
 * @param closeModal Callback to force-close any open modal before redirecting to login
 * @returns `requireLogin` and `goBackFromLogin` helpers
 */
export function useAuthNavigation(closeModal: (forced?: boolean) => void = () => {}) {
    const router = useRouter();
    const route = useRoute();
    let requiredLoginInProgress = false;

    /** Clears the session and sends the user to the login page. */
    const requireLogin = () => {
        requireLoginNavigation(
            router,
            route,
            closeModal,
            () => requiredLoginInProgress,
            (value) => {
                requiredLoginInProgress = value;
            },
        );
    };

    /** Returns to the route stored before login was required. */
    const goBackFromLogin = () => {
        goBackFromLoginNavigation(router);
    };

    return { requireLogin, goBackFromLogin };
}
